import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { CalendarCheckIcon, CalendarClockIcon, CalendarPlusIcon, HistoryIcon, HourglassIcon, PlaneIcon, SunIcon } from 'lucide-react';
import { timeOffEntries } from '../../data/timeOff';
import type { Employee, Navigate, PtoRequest } from '../../types/hr';
import { TODAY, formatDate, formatDays, formatRange } from '../../utils/format';
import { accrualStart, balanceFor, getPolicy, historyFor, scheduledDays, usedYtd } from '../../utils/timeOff';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { EmptyState } from '../ui/EmptyState';
import { FilterSelect } from '../ui/FilterSelect';
import { TbdBadge } from '../ui/TbdBadge';
interface TimeOffTabProps {
  employee: Employee;
  requests: PtoRequest[];
  onRequest: () => void;
  onNavigate: Navigate;
}
export function TimeOffTab({
  employee,
  requests,
  onRequest,
  onNavigate
}: TimeOffTabProps) {
  const policy = getPolicy(employee.policyId);
  const balance = balanceFor(employee.id);
  const history = useMemo(() => historyFor(employee), [employee]);
  const years = Array.from(new Set(history.map(h => String(h.year))));
  const [year, setYear] = useState(years[0] ?? '2026');
  const [policyFilter, setPolicyFilter] = useState(policy?.name ?? 'All');
  const used = usedYtd(employee);
  const scheduled = scheduledDays(employee.id);
  const isNewHire = employee.policyId === 'newhire';
  const isRegular = employee.policyId === 'regular';
  const accrualDate = accrualStart(employee.hireDate);
  const todayIso = format(TODAY, 'yyyy-MM-dd');
  const upcoming = [...timeOffEntries.filter(e => e.employeeId === employee.id && e.end >= todayIso).map(e => ({
    id: e.id,
    start: e.start,
    end: e.end,
    days: e.days,
    label: e.policyLabel,
    pending: false
  })), ...requests.filter(r => r.employeeId === employee.id && r.status !== 'denied').map(r => ({
    id: r.id,
    start: r.start,
    end: r.end,
    days: r.days,
    label: getPolicy(r.policyId)?.name ?? '',
    pending: r.status === 'pending'
  }))].sort((a, b) => a.start.localeCompare(b.start));
  const rows = history.filter(h => String(h.year) === year);
  return <div className="space-y-6" data-id="element-453" data-mp="24jgef2x7j1rf">
      {policy && balance ? <div className="grid grid-cols-1 gap-6 xl:grid-cols-3" data-id="element-454" data-mp="28mv2mgvauofy">
          <div className="relative overflow-hidden rounded-2xl bg-white p-6 shadow-soft ring-1 ring-slate-900/[0.04] xl:col-span-2" data-id="element-455" data-mp="2dlpknglg08z0">
            <div className="absolute right-0 top-0 h-full w-1.5 bg-flame" aria-hidden="true" data-id="element-456" data-mp="2ik0tbo004dfn" />
            <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-457" data-mp="2nb93wnd2i4ue">
              <div data-id="element-458" data-mp="2sacbofkfmp62">
                <div className="flex items-center gap-2" data-id="element-459" data-mp="2savj32cghv5u">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-azure/10 text-azure" data-id="element-460" data-mp="2scex0man3u4g">
                    <SunIcon className="h-4 w-4" data-id="element-461" data-mp="31ba7wbompse3" />
                  </span>
                  <button type="button" onClick={() => onNavigate({
                page: 'policy',
                policyId: policy.id
              })} className="text-sm font-semibold text-navy hover:text-azure" data-id="element-462" data-mp="3197intvaw3af">
                    {policy.name}
                  </button>
                  {policy.hasTbd && <TbdBadge data-id="element-463" data-mp="gmgx1rugnung" />}
                </div>
                <p className="mt-5 flex items-baseline gap-2" data-id="element-464" data-mp="gpcvrie4uncy">
                  <span className="text-5xl font-semibold tracking-tight text-navy" data-id="element-465" data-mp="bph0q8ney69j">{balance.available}</span>
                  <span className="text-lg font-medium text-slate-500" data-id="element-466" data-mp="18dws8aa32ppa">days</span>
                </p>
                <p className="mt-1 text-sm text-muted" data-id="element-467" data-mp="14dcub4hori23">Available balance</p>
              </div>
              <Button variant="primary" icon={CalendarPlusIcon} onClick={onRequest} data-id="element-468" data-mp="zkjqciu1kdr2">
                Request time off
              </Button>
            </div>
            <dl className="mt-6 grid grid-cols-3 gap-4 border-t border-slate-100 pt-5" data-id="element-469" data-mp="1s2xflu1barej">
              <div data-id="element-470" data-mp="1nbyxcowrjj8n">
                <dt className="text-xs text-muted" data-id="element-471" data-mp="1nbfdh2njykrz">Used YTD</dt>
                <dd className="mt-1 text-lg font-semibold text-navy" data-id="element-472" data-mp="1n9ui5ateyxt2">{formatDays(used)}</dd>
              </div>
              <div data-id="element-473" data-mp="1bv9asvxu60iu">
                <dt className="text-xs text-muted" data-id="element-474" data-mp="1bvj68z1ngzvx">Scheduled</dt>
                <dd className="mt-1 text-lg font-semibold text-navy" data-id="element-475" data-mp="1bswpc8svd811">{formatDays(scheduled)}</dd>
              </div>
              <div data-id="element-476" data-mp="12ub6ecjhjqzq">
                <dt className="text-xs text-muted" data-id="element-477" data-mp="12tt531hyumky">After scheduled</dt>
                <dd className="mt-1 text-lg font-semibold text-navy" data-id="element-478" data-mp="1qk5mr430jxoq">{formatDays(balance.available - scheduled)}</dd>
              </div>
            </dl>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-1" data-id="element-479" data-mp="1ukz5lj1lmmar">
            <div className="rounded-2xl bg-white p-5 shadow-soft ring-1 ring-slate-900/[0.04]" data-id="element-480" data-mp="1gtufp8o198dm">
              <div className="flex items-center gap-2 text-xs font-medium text-muted" data-id="element-481" data-mp="1ls5mfaj172rm">
                {isNewHire ? <HourglassIcon className="h-4 w-4 text-flame" data-id="element-482" data-mp="29hgi4dliy6yw" /> : <CalendarClockIcon className="h-4 w-4 text-azure" data-id="element-483" data-mp="2e9hqhofkndjn" />}
                {isNewHire ? 'Waiting period' : 'Accrual'}
              </div>
              {isNewHire ? <p className="mt-2 text-lg font-semibold text-navy" data-id="element-484" data-mp="2nm71mczbq2ee">Begins {format(accrualDate, 'MMM d')}</p> : <div className="mt-2 flex items-center gap-2" data-id="element-485" data-mp="2xewb52bbd673">
                  <span className="text-lg font-semibold text-navy" data-id="element-486" data-mp="2xgh44nhzch2u">Rate</span>
                  <TbdBadge data-id="element-487" data-mp="2sftfu9l4yecb" />
                </div>}
              <p className="mt-0.5 text-xs text-muted" data-id="element-488" data-mp="24oxhsuq36mwm">{isNewHire ? '1 day / month after 30 days, prorated' : 'Posted monthly once leadership sets the rate'}</p>
            </div>
            <div className="rounded-2xl bg-white p-5 shadow-soft ring-1 ring-slate-900/[0.04]" data-id="element-489" data-mp="2enlncu0vx9jf">
              <div className="flex items-center gap-2 text-xs font-medium text-muted" data-id="element-490" data-mp="29osbf618xqld">
                <CalendarCheckIcon className="h-4 w-4 text-deep" data-id="element-491" data-mp="29r4whs2awh2n" />
                {isNewHire ? 'First anniversary' : 'Work anniversary'}
              </div>
              <p className="mt-2 text-lg font-semibold text-navy" data-id="element-492" data-mp="3l35sns1hv7sf">{formatDate(employee.hireDate, 'MMM d')}</p>
              <p className="mt-0.5 text-xs text-muted" data-id="element-493" data-mp="3t43m7jiq8mur">{isNewHire ? 'Balance tops up to 15 days' : 'Anniversary adjustment TBD'}</p>
            </div>
          </div>
        </div> : <Card title="Time off balance" icon={SunIcon} data-id="element-494" data-mp="618n8z8k2eni">
          <EmptyState icon={SunIcon} title="No time off policy assigned" description="Regular PTO currently covers the Operations pilot group, and new hires are enrolled in New Hire PTO." action={<Button size="sm" onClick={() => onNavigate({
        page: 'timeoff',
        tab: 'policies'
      })} data-id="element-496" data-mp="3ega7e11gtod8">
                View policies
              </Button>} data-id="element-495" data-mp="ayrzxuplujdc" />
        </Card>}

      <Card title="Upcoming time off" icon={PlaneIcon} subtitle={upcoming.length ? `${upcoming.length} scheduled` : undefined} bodyClassName="px-3 pb-3" data-id="element-497" data-mp="3o74vlay9uy1b">
        {upcoming.length === 0 ? <div className="px-3 pb-3" data-id="element-498" data-mp="2vny85n63z37b">
            <EmptyState icon={PlaneIcon} title="Nothing scheduled" compact data-id="element-499" data-mp="2vmwjuh030k7n" />
          </div> : <ul className="space-y-0.5" data-id="element-500" data-mp="2j6nghb80397u">
            {upcoming.map(u => <li key={u.id} className="flex items-center gap-4 rounded-xl px-3 py-3 hover:bg-canvas/70" data-id="element-501" data-mp="2j7f3vyrg20n1">
                <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl bg-navy/[0.05] text-navy" data-id="element-502" data-mp="2e9n98wzf00f5">
                  <span className="text-[10px] font-semibold uppercase leading-none" data-id="element-503" data-mp="256m717do6tgr">{formatDate(u.start, 'MMM')}</span>
                  <span className="text-lg font-semibold leading-tight" data-id="element-504" data-mp="20f4en7ppq17e">{formatDate(u.start, 'd')}</span>
                </div>
                <div className="min-w-0 flex-1" data-id="element-505" data-mp="1vhkmd3w6jnwg">
                  <p className="text-sm font-medium text-navy" data-id="element-506" data-mp="1qi80ald4ukux">{formatRange(u.start, u.end)}</p>
                  <p className="text-xs text-muted" data-id="element-507" data-mp="1lin2wfdm2kkb">
                    {u.label} · {formatDays(u.days)}
                  </p>
                </div>
                <Badge tone={u.pending ? 'orange' : 'green'} dot data-id="element-508" data-mp="1hhtezm0655rf">
                  {u.pending ? 'Pending' : 'Approved'}
                </Badge>
              </li>)}
          </ul>}
      </Card>

      <Card title="History" icon={HistoryIcon} bodyClassName="pb-2" action={history.length > 0 && <div className="flex gap-2" data-id="element-510" data-mp="30ed7e0ck40r9">
              <FilterSelect label="Policy" value={policyFilter} options={[policy?.name ?? 'All']} onChange={setPolicyFilter} className="w-40" data-id="element-511" data-mp="30d1v1c51pkoj" />
              <FilterSelect label="Year" value={year} options={years} onChange={setYear} className="w-24" data-id="element-512" data-mp="2rdyd4bhm7bbm" />
            </div>} data-id="element-509" data-mp="3acauulmldtit">
        {isRegular && <p className="mx-6 mb-3 flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-2.5 text-xs text-amber-800 ring-1 ring-inset ring-amber-200" data-id="element-513" data-mp="2cnhc2t8w6nf9">
            <TbdBadge data-id="element-514" data-mp="23pov7kniv7pr" /> Regular PTO accrual rate is TBD — accrual rows are recorded without amounts.
          </p>}
        {rows.length === 0 ? <div className="px-6 pb-4" data-id="element-515" data-mp="1p1x8bkv5pa87">
            <EmptyState icon={HistoryIcon} title="No history yet" compact data-id="element-516" data-mp="1p2yh1iop9kmr" />
          </div> : <div className="overflow-x-auto" data-id="element-517" data-mp="1k45g0pqlnm3h">
            <table className="w-full min-w-[640px] text-left text-sm" data-id="element-518" data-mp="1k56smyn9kgnb">
              <thead data-id="element-519" data-mp="1y46czu3qu9do">
                <tr className="border-y border-slate-100 bg-canvas/70 text-[11px] font-semibold uppercase tracking-wider text-muted" data-id="element-520" data-mp="1y08v1bswjnvd">
                  <th scope="col" className="px-6 py-2.5" data-id="element-521" data-mp="2809jtd9jiw6y">Date</th>
                  <th scope="col" className="px-6 py-2.5" data-id="element-522" data-mp="27yg9nnzpxtu7">Description</th>
                  <th scope="col" className="px-6 py-2.5 text-right" data-id="element-523" data-mp="231g2tav1on21">Used (-)</th>
                  <th scope="col" className="px-6 py-2.5 text-right" data-id="element-524" data-mp="2grtbjffrg5sf">Earned (+)</th>
                  <th scope="col" className="px-6 py-2.5 text-right" data-id="element-525" data-mp="2cskgm78zi1va">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-526" data-mp="2qrshr5opjk24">
                {rows.map(h => <tr key={h.id} className="transition-colors hover:bg-azure/[0.03]" data-id="element-527" data-mp="2ltqzuo30pqs8">
                    <td className="whitespace-nowrap px-6 py-3 text-slate-600" data-id="element-528" data-mp="2ear0d8ygikl0">{formatDate(h.date, 'MM/dd/yyyy')}</td>
                    <td className="px-6 py-3 text-slate-800" data-id="element-529" data-mp="29ho1clawuixf">{h.description}</td>
                    <td className="px-6 py-3 text-right tabular-nums text-rose-600" data-id="element-530" data-mp="25ifbhrizr575">{h.used ? `-${h.used.toFixed(2)}` : ''}</td>
                    <td className="px-6 py-3 text-right tabular-nums text-emerald-600" data-id="element-531" data-mp="20gg66c3oxy7k">
                      {h.pendingAccrual ? <TbdBadge data-id="element-532" data-mp="2x6h3x0m1fp10" /> : h.earned ? `+${h.earned.toFixed(2)}` : ''}
                    </td>
                    <td className="px-6 py-3 text-right font-semibold tabular-nums text-navy" data-id="element-533" data-mp="2t5dq0jnjw32f">{h.balance.toFixed(2)}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>
    </div>;
}